import { ReactiveVar } from '@apollo/client'
import { localStorage } from '@emico-utils/ssr-utils'

import onReactiveVarChange from './onReactiveVarChange'

function writeToStorage<T>(storageKey: string, data: T) {
  const stringified = JSON.stringify(data)

  // Check if the value is already the same to prevent storage event loops
  const currentValue = localStorage?.getItem(storageKey)
  if (
    currentValue === stringified ||
    (currentValue === null && stringified === undefined)
  ) {
    return
  }

  // If the nextValue is undefined, we need to remove the item from storage.
  // Note that null is a valid value, and should be stored
  if (stringified === undefined) {
    localStorage?.removeItem(storageKey)
  } else {
    localStorage?.setItem(storageKey, stringified)
  }
}

/**
 * Keeps a reactive var in sync with localStorage. Values are stored as JSON
 * so it is not possible to store stuff like circular Objects, functions e.g.
 *
 * @param rv reactiveVar to persist
 * @param storageKey localStorage key
 */
export function persistReactiveVar<T>(rv: ReactiveVar<T>, storageKey: string) {
  // If the var changes, store its value
  const removeListener = onReactiveVarChange(rv, (nextValue) => {
    writeToStorage(storageKey, nextValue)
  })

  // The reactive var could already have a value
  if (rv() !== undefined) {
    writeToStorage(storageKey, rv())
  }

  // Return a function which can be used to stop listening to changes
  return () => {
    removeListener()
  }
}
